import { ScanSearch, Keyboard, Network, Eye, FileText, Landmark } from "lucide-react";
import { useFlowReveal } from "@/hooks/useFlowReveal";

const frameworkSteps = [
  { name: "Assess", detail: "Automated and manual audit, with every finding rated by severity." },
  { name: "Remediate", detail: "Fixes in code, design and PDFs, guided by the action plan." },
  { name: "Implement", detail: "Accessible components and checks built into your release process." },
  { name: "Conform", detail: "Re-test and issue the conformance report or VPAT." },
  { name: "Sustain", detail: "Monitoring and team training so it stays fixed." },
];

// What the scanner actually runs on every page (see the scan pipeline), so the
// platform is described by its checks rather than by adjectives.
const platformPoints = [
  { icon: ScanSearch, label: "WCAG rules, automated", detail: "axe-core checks mapped to your chosen level and version, up to WCAG 2.2." },
  { icon: Keyboard, label: "Keyboard and focus", detail: "Visible focus, keyboard traps and skip links, tested by actually tabbing through." },
  { icon: Network, label: "Accessibility tree", detail: "Page title, headings, landmarks, labels, names and alt text as assistive tech sees them." },
  { icon: Eye, label: "AI visual review", detail: "Screenshots reviewed for problems code cannot show, such as meaning carried by colour alone." },
  { icon: FileText, label: "PDF analysis", detail: "Linked PDFs are found and examined as documents, not skipped." },
  { icon: Landmark, label: "India checks", detail: "GIGW conformance items such as sitemap, robots, accessibility statement and page language." },
];

const DeliverSection = () => {
  const frameworkRef = useFlowReveal<HTMLOListElement>();
  const platformRef = useFlowReveal<HTMLUListElement>();

  return (
  <section id="deliver" className="section-padding" aria-labelledby="deliver-heading">
    <div className="max-w-7xl mx-auto">
      <p className="eyebrow mb-4">How We Deliver</p>
      <h2 id="deliver-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-8">
        Framework. Platform.
      </h2>

      <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
        {/* Framework */}
        <div className="card-surface p-6 md:p-8 lg:col-span-2">
          <h3 className="font-display text-lg font-bold text-foreground mb-6">Framework</h3>
          <ol ref={frameworkRef} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5" aria-label="Framework steps">
            {frameworkSteps.map((step, i) => (
              <li key={step.name} style={{ "--i": i } as React.CSSProperties}>
                <div className="flow-rule h-0.5 bg-foreground" aria-hidden="true" />
                <div className="flow-step pt-4">
                  <span className="font-mono text-sm font-bold text-primary" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h4 className="font-display text-lg font-bold text-foreground">{step.name}</h4>
                  <p className="mt-1 text-base leading-relaxed text-muted-foreground">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Platform */}
        <div className="card-surface p-6 md:p-8 lg:col-span-2">
          <h3 className="font-display text-lg font-bold text-foreground mb-6">Platform: what it checks</h3>
          <ul ref={platformRef} className="grid gap-x-12 gap-y-6 md:grid-cols-2">
            {platformPoints.map((p, i) => (
              <li key={p.label} className="flow-step flex items-start gap-4" style={{ "--i": i } as React.CSSProperties}>
                <p.icon size={20} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  <p className="text-base font-semibold text-foreground">{p.label}</p>
                  <p className="text-base leading-relaxed text-muted-foreground">{p.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
  );
};

export default DeliverSection;
